import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Activity,
  CircleHelp,
  Clock3,
  Monitor,
  SlidersHorizontal,
  Smartphone,
  Tablet,
} from "lucide-react";
import { CompletionByDeviceKey } from "../api/contracts";
import { PortalDataTable, PortalTableColumn } from "./DataDisplay";

function joinClassNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(" ");
}

export function ReportMetricCard({
  label,
  value,
  detail,
  tone,
  className,
  children,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  detail?: React.ReactNode;
  tone?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <article
      className={joinClassNames(
        "cp-metric-card",
        "cp-report-metric-card",
        className,
      )}
      data-tone={tone}
    >
      {typeof label === "string" || typeof label === "number" ? (
        <span>{label}</span>
      ) : (
        label
      )}
      <strong>{value}</strong>
      {children}
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

export function CaseloadMetricCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone: "messages" | "homework" | "inactive" | "inactive-clear";
}) {
  return (
    <ReportMetricCard
      className="cp-caseload-metric-card"
      label={label}
      value={value}
      tone={tone}
    />
  );
}

export function ReportWindowBanner({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={joinClassNames("cp-report-window-banner", className)}
      role="note"
    >
      {children}
    </div>
  );
}

export function ReportTableSection<TRecord>({
  title,
  emptyMessage,
  columns,
  records,
  getRecordKey,
  className,
  onRecordOpen,
  getRecordOpenLabel,
}: {
  title: string;
  emptyMessage: string;
  columns: readonly PortalTableColumn<TRecord>[];
  records: readonly TRecord[];
  getRecordKey: (record: TRecord) => string;
  className?: string;
  onRecordOpen?: (record: TRecord) => void;
  getRecordOpenLabel?: (record: TRecord) => string;
}) {
  const headingId = useId();
  return (
    <section
      className={joinClassNames("cp-report-table-section", className)}
      aria-labelledby={headingId}
    >
      <h3 id={headingId}>{title}</h3>
      {records.length ? (
        <PortalDataTable
          columns={columns}
          records={records}
          getRecordKey={getRecordKey}
          caption={title}
          onRecordOpen={onRecordOpen}
          getRecordOpenLabel={getRecordOpenLabel}
        />
      ) : (
        <p className="cp-report-table-section__empty" role="status">
          {emptyMessage}
        </p>
      )}
    </section>
  );
}

function formatRate(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

function formatNumber(value: number | null): string {
  return value === null ? "—" : value.toFixed(0);
}

function formatSignedNumber(value: number | null): string {
  if (value === null) return "—";
  const roundedValue = Number(value.toFixed(0));
  return `${roundedValue > 0 ? "+" : ""}${roundedValue}`;
}

function formatMinutes(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)} min`;
}

function MetricLabel({
  children,
  definition,
}: {
  children: string;
  definition: string;
}) {
  return (
    <span className="cp-report-label-with-tooltip">
      {children}
      <ReportHelpTooltip label={children} definition={definition} />
    </span>
  );
}

export function ReportHelpTooltip({
  label,
  definition,
  placement = "top",
  ariaLabel,
  trigger,
}: {
  label: string;
  definition: string;
  placement?: "top" | "bottom";
  ariaLabel?: string;
  trigger?: React.ReactNode;
}) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: -9999,
    left: -9999,
    arrowLeft: 0,
    placement,
  });

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const tooltip = tooltipRef.current;
      if (!trigger || !tooltip) return;

      const viewportPadding = 16;
      const gap = 9;
      const triggerRect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width;
      const tooltipHeight = tooltipRect.height;
      const spaceAbove = triggerRect.top - viewportPadding;
      const spaceBelow =
        window.innerHeight - triggerRect.bottom - viewportPadding;
      let resolvedPlacement = placement;

      if (
        placement === "top" &&
        tooltipHeight + gap > spaceAbove &&
        spaceBelow > spaceAbove
      ) {
        resolvedPlacement = "bottom";
      } else if (
        placement === "bottom" &&
        tooltipHeight + gap > spaceBelow &&
        spaceAbove > spaceBelow
      ) {
        resolvedPlacement = "top";
      }

      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const maximumLeft = Math.max(
        viewportPadding,
        window.innerWidth - tooltipWidth - viewportPadding,
      );
      const left = Math.min(
        maximumLeft,
        Math.max(viewportPadding, triggerCenter - tooltipWidth / 2),
      );
      const preferredTop =
        resolvedPlacement === "top"
          ? triggerRect.top - tooltipHeight - gap
          : triggerRect.bottom + gap;
      const maximumTop = Math.max(
        viewportPadding,
        window.innerHeight - tooltipHeight - viewportPadding,
      );
      const top = Math.min(maximumTop, Math.max(viewportPadding, preferredTop));
      const arrowLeft = Math.min(
        Math.max(12, tooltipWidth - 12),
        Math.max(12, triggerCenter - left),
      );

      setPosition({ top, left, arrowLeft, placement: resolvedPlacement });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [definition, isOpen, placement]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="cp-homework-overview-tooltip"
        aria-label={ariaLabel ?? `About ${label}`}
        aria-describedby={tooltipId}
        data-placement={placement}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          setIsOpen(false);
          event.currentTarget.blur();
        }}
      >
        {trigger ?? <CircleHelp aria-hidden="true" />}
      </button>
      {typeof document === "undefined"
        ? null
        : createPortal(
            <span
              ref={tooltipRef}
              id={tooltipId}
              className="cp-homework-overview-tooltip__content"
              role="tooltip"
              aria-hidden={isOpen ? undefined : "true"}
              data-open={isOpen ? "true" : "false"}
              data-placement={position.placement}
              style={
                {
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  "--cp-tooltip-arrow-left": `${position.arrowLeft}px`,
                } as React.CSSProperties
              }
            >
              {definition}
            </span>,
            document.body,
          )}
    </>
  );
}

const sudsGaugeBands = [
  { from: 180, to: 144, color: "#22c55e" },
  { from: 144, to: 108, color: "#84cc16" },
  { from: 108, to: 72, color: "#facc15" },
  { from: 72, to: 36, color: "#fb923c" },
  { from: 36, to: 0, color: "#ef4444" },
] as const;

function sudsGaugePoint(angleDegrees: number, radius: number) {
  const radians = (angleDegrees * Math.PI) / 180;
  return {
    x: 80 + radius * Math.cos(radians),
    y: 78 - radius * Math.sin(radians),
  };
}

function sudsGaugeArc(startAngle: number, endAngle: number) {
  const start = sudsGaugePoint(startAngle, 52);
  const end = sudsGaugePoint(endAngle, 52);
  return `M ${start.x} ${start.y} A 52 52 0 0 1 ${end.x} ${end.y}`;
}

function ReadOnlySudsGauge({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  const normalizedValue =
    value === null ? null : Math.min(10, Math.max(0, value));
  const needleAngle =
    normalizedValue === null ? null : 180 - (normalizedValue / 10) * 180;
  const needleTip =
    needleAngle === null ? null : sudsGaugePoint(needleAngle, 39);
  const displayValue = formatNumber(value);

  return (
    <div className="cp-homework-overview-suds-gauge">
      <span>{label}</span>
      <div
        className="cp-homework-overview-suds-gauge__meter"
        role={normalizedValue === null ? "img" : "meter"}
        aria-label={`${label}: ${
          normalizedValue === null ? "No data" : `${displayValue} out of 10`
        }`}
        aria-valuemin={normalizedValue === null ? undefined : 0}
        aria-valuemax={normalizedValue === null ? undefined : 10}
        aria-valuenow={normalizedValue ?? undefined}
      >
        <svg viewBox="0 0 160 96" aria-hidden="true">
          {sudsGaugeBands.map((band) => (
            <path
              key={band.from}
              d={sudsGaugeArc(band.from, band.to)}
              fill="none"
              stroke={band.color}
              strokeWidth="12"
            />
          ))}
          <text x="17" y="91">
            0
          </text>
          <text x="80" y="17">
            5
          </text>
          <text x="143" y="91">
            10
          </text>
          {needleTip ? (
            <>
              <line
                x1="80"
                y1="78"
                x2={needleTip.x}
                y2={needleTip.y}
                className="cp-homework-overview-suds-gauge__needle"
              />
              <circle
                cx="80"
                cy="78"
                r="5"
                className="cp-homework-overview-suds-gauge__hub"
              />
            </>
          ) : null}
        </svg>
        <strong>{displayValue}</strong>
      </div>
    </div>
  );
}

export interface HomeworkOverviewSummaryVisualData {
  assigned: number;
  completed: number;
  open: number;
  overdue: number;
  completionRate: number | null;
  selfInitiated: number;
}

export interface HomeworkOverviewClinicalVisualData {
  copingSkillAppliedCount: number;
  copingSkillAppliedDenominator: number;
  copingSkillAppliedRate: number | null;
  alteredRoutineCount: number;
  alteredRoutineDenominator: number;
  alteredRoutineRate: number | null;
  pairedSudsCount: number;
  averagePreSuds: number | null;
  averagePostSuds: number | null;
  averageSudsChange: number | null;
  routineTimeRecordedCount: number;
  averageRoutineTime: number | null;
  medianRoutineTime: number | null;
}

export function HomeworkOverviewMetrics({
  summary,
  clinicalMetrics,
}: {
  summary: HomeworkOverviewSummaryVisualData;
  clinicalMetrics: HomeworkOverviewClinicalVisualData;
}) {
  return (
    <>
      <div className="cp-homework-overview-primary-grid">
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="assigned"
          label={
            <MetricLabel definition="All homework assigned during the selected period. Homework without a due date is included.">
              Assigned
            </MetricLabel>
          }
          value={summary.assigned}
          detail="Based on assigned date"
        />
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="completed"
          label={
            <MetricLabel definition="Assignments currently marked Completed or Completed with Modification.">
              Completed
            </MetricLabel>
          }
          value={summary.completed}
          detail="Current completion status"
        />
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="open"
          label={
            <MetricLabel definition="Assignments in the cohort that are not currently completed.">
              Open
            </MetricLabel>
          }
          value={summary.open}
          detail="Assigned minus completed"
        />
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="overdue"
          label={
            <MetricLabel definition="A subset of Open: homework with a due date before the report's local as-of date. Open homework without a due date is not overdue.">
              Overdue
            </MetricLabel>
          }
          value={summary.overdue}
          detail="Subset of Open"
        />
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="completion-rate"
          label={
            <MetricLabel definition="Completed divided by Assigned. Because the cohort uses current status, this rate can change when older assignments are later completed.">
              Completion Rate
            </MetricLabel>
          }
          value={formatRate(summary.completionRate)}
          detail={`${summary.completed} of ${summary.assigned} assignments`}
        >
          {summary.completionRate !== null ? (
            <div
              className="cp-homework-overview-progress"
              role="progressbar"
              aria-label="Completion rate"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={summary.completionRate}
            >
              <span style={{ width: `${summary.completionRate}%` }} />
            </div>
          ) : null}
        </ReportMetricCard>
        <ReportMetricCard
          className="cp-homework-overview-metric"
          tone="self-initiated"
          label={
            <MetricLabel definition="Assignments in the selected cohort that were not created by a therapist.">
              Self-Initiated
            </MetricLabel>
          }
          value={summary.selfInitiated}
          detail="Within the assigned cohort"
        />
      </div>

      <div className="cp-homework-overview-clinical-grid">
        <section
          className="cp-homework-overview-clinical-card"
          data-tone="suds"
        >
          <div className="cp-homework-overview-clinical-card__header">
            <span className="cp-homework-overview-clinical-card__icon">
              <Activity aria-hidden="true" />
            </span>
            <div>
              <h3>SUDS Change</h3>
              <p>
                Uses activities with both valid pre- and post-exposure ratings.
              </p>
            </div>
          </div>
          <div className="cp-homework-overview-suds-gauges">
            <ReadOnlySudsGauge
              label="Average Pre-SUDS"
              value={clinicalMetrics.averagePreSuds}
            />
            <ReadOnlySudsGauge
              label="Average Post-SUDS"
              value={clinicalMetrics.averagePostSuds}
            />
          </div>
          <dl className="cp-homework-overview-suds-summary">
            <div>
              <dt>Activities with Both Ratings</dt>
              <dd>{clinicalMetrics.pairedSudsCount}</dd>
            </div>
            <div
              className="cp-homework-overview-suds-change"
              data-tone={
                clinicalMetrics.averageSudsChange === null ||
                clinicalMetrics.averageSudsChange === 0
                  ? "neutral"
                  : clinicalMetrics.averageSudsChange < 0
                    ? "improved"
                    : "worsened"
              }
            >
              <dt>
                Average SUDS Change
                <ReportHelpTooltip
                  label="Average SUDS Change"
                  definition="Average post-SUDS minus pre-SUDS for assignments containing both valid ratings."
                />
              </dt>
              <dd>{formatSignedNumber(clinicalMetrics.averageSudsChange)}</dd>
            </div>
          </dl>
        </section>

        <section
          className="cp-homework-overview-clinical-card"
          data-tone="adaptation"
        >
          <div className="cp-homework-overview-clinical-card__header">
            <span className="cp-homework-overview-clinical-card__icon">
              <SlidersHorizontal aria-hidden="true" />
            </span>
            <div>
              <h3>Homework Adaptation</h3>
              <p>Rates use completed cohort assignments as the denominator.</p>
            </div>
          </div>
          <dl>
            <div>
              <dt>Coping skill applied</dt>
              <dd>{formatRate(clinicalMetrics.copingSkillAppliedRate)}</dd>
              <small>
                {clinicalMetrics.copingSkillAppliedCount} of{" "}
                {clinicalMetrics.copingSkillAppliedDenominator}
              </small>
            </div>
            <div>
              <dt>Altered routine</dt>
              <dd>{formatRate(clinicalMetrics.alteredRoutineRate)}</dd>
              <small>
                {clinicalMetrics.alteredRoutineCount} of{" "}
                {clinicalMetrics.alteredRoutineDenominator}
              </small>
            </div>
          </dl>
        </section>

        <section
          className="cp-homework-overview-clinical-card"
          data-tone="routine"
        >
          <div className="cp-homework-overview-clinical-card__header">
            <span className="cp-homework-overview-clinical-card__icon">
              <Clock3 aria-hidden="true" />
            </span>
            <div>
              <h3>Routine Time</h3>
              <p>Valid recorded durations, normalized to minutes.</p>
            </div>
          </div>
          <dl>
            <div>
              <dt>Recorded activities</dt>
              <dd>{clinicalMetrics.routineTimeRecordedCount}</dd>
            </div>
            <div>
              <dt>Average</dt>
              <dd>{formatMinutes(clinicalMetrics.averageRoutineTime)}</dd>
            </div>
            <div>
              <dt>Median</dt>
              <dd>{formatMinutes(clinicalMetrics.medianRoutineTime)}</dd>
            </div>
          </dl>
        </section>
      </div>
    </>
  );
}

export interface DeviceBreakdownVisualItem {
  key: CompletionByDeviceKey;
  label: string;
  count: number;
  percentage: number;
}

const deviceIcons: Record<CompletionByDeviceKey, React.ReactNode> = {
  desktop_laptop: <Monitor aria-hidden="true" />,
  iphone: <Smartphone aria-hidden="true" />,
  android_phone: <Smartphone aria-hidden="true" />,
  ipad: <Tablet aria-hidden="true" />,
  android_tablet: <Tablet aria-hidden="true" />,
  unknown: <CircleHelp aria-hidden="true" />,
};

export function CompletionByDeviceChart({
  rows,
  emptyDescription = "Try a different client, activity, or completed-date range.",
}: {
  rows: readonly DeviceBreakdownVisualItem[];
  emptyDescription?: string;
}) {
  if (!rows.length) {
    return (
      <div className="cp-device-report__empty" role="status">
        <Monitor aria-hidden="true" />
        <h3>No completed homework in this period</h3>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div
      className="cp-device-report-chart"
      role="img"
      aria-label={`Completed assignments by device. ${rows
        .map(
          (item) =>
            `${item.label}: ${item.count}, ${item.percentage.toFixed(1)} percent`,
        )
        .join(". ")}`}
    >
      {rows.map((item) => (
        <div
          key={item.key}
          className="cp-device-report-chart__row"
          data-device={item.key}
        >
          <div className="cp-device-report-chart__label">
            <span className="cp-device-report-chart__icon">
              {deviceIcons[item.key]}
            </span>
            <span>{item.label}</span>
          </div>
          <div className="cp-device-report-chart__track" aria-hidden="true">
            <span style={{ width: `${item.percentage}%` }} />
          </div>
          <div className="cp-device-report-chart__value">
            <strong>{item.count}</strong>
            <span>{item.percentage.toFixed(1)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export interface ValueEngagementCohortVisualData {
  rate: number | null;
  engaged: number;
  sampleSize: number;
}

function differenceLabel(value: number | null): string {
  if (value === null) {
    return "Both groups need at least one assignment before they can be compared.";
  }
  if (value === 0) {
    return "The observed engagement rates are the same for both groups.";
  }
  const direction = value > 0 ? "higher" : "lower";
  return `Value-linked homework is ${Math.abs(value).toFixed(1)} percentage points ${direction} in this sample.`;
}

export function ValueEngagementMetrics({
  linked,
  unlinked,
}: {
  linked: ValueEngagementCohortVisualData;
  unlinked: ValueEngagementCohortVisualData;
}) {
  const difference =
    linked.rate === null || unlinked.rate === null
      ? null
      : linked.rate - unlinked.rate;

  return (
    <>
      <div className="cp-value-engagement-kpi-grid">
        <ReportMetricCard
          className="cp-value-engagement-metric"
          tone="value-linked"
          label="Value-Linked Engagement Rate"
          value={formatRate(linked.rate)}
          detail={`${linked.engaged} engaged of ${linked.sampleSize}`}
        />
        <ReportMetricCard
          className="cp-value-engagement-metric"
          label="No Value-Linked Engagement Rate"
          value={formatRate(unlinked.rate)}
          detail={`${unlinked.engaged} engaged of ${unlinked.sampleSize}`}
        />
        <ReportMetricCard
          className="cp-value-engagement-metric"
          label="Value-Linked Sample Size"
          value={linked.sampleSize}
          detail="Terminal homework outcomes"
        />
        <ReportMetricCard
          className="cp-value-engagement-metric"
          label="No Value-Linked Sample Size"
          value={unlinked.sampleSize}
          detail="Terminal homework outcomes"
        />
      </div>
      <p className="cp-value-engagement-report__difference">
        <strong>Observed difference:</strong> {differenceLabel(difference)}
      </p>
    </>
  );
}
